import { Box, Button, Tab, Tabs } from '@mui/material';
import React, { useState } from 'react';
import { Link as RouterLink, Navigate, useParams } from 'react-router-dom';
import { compactMoney, date } from '../../lib/format';
import { BASIS_LABELS } from '../../lib/labels';
import { sumBy, useTenantScope } from '../../store/selectors';
import { PageHeader, StatCard, StatGrid, StatusChip } from '../../ui/primitives';
import HoldingsTable from '../common/HoldingsTable';
import FundClassesTab from './FundClassesTab';
import FundEventsTab from './FundEventsTab';
import FundSettingsTab from './FundSettingsTab';

export default function FundDetailPage() {
  const { fundId } = useParams();
  const scope = useTenantScope();
  const [tab, setTab] = useState(0);
  const fund = fundId ? scope.fundById.get(fundId) : undefined;
  if (!fund) return <Navigate to="/manager/funds" replace />;

  const holdings = scope.holdings.filter((holding) => holding.fundId === fund.id);
  const value = sumBy(holdings, (holding) => holding.value);
  const income = sumBy(
    scope.ledger.filter(
      (entry) =>
        entry.fundId === fund.id &&
        entry.type === 'DISTRIBUTION' &&
        entry.effectiveDate >= '2026-07-01',
    ),
    (entry) => entry.amount,
  );
  const uncalled = sumBy(holdings, (holding) => holding.uncalled ?? 0);

  return (
    <>
      <PageHeader
        eyebrow={`${fund.code} · ${fund.strategy} · ${BASIS_LABELS[fund.basis]}`}
        title={
          <>
            {fund.name} <StatusChip status={fund.status} />
          </>
        }
        subtitle={fund.description}
        actions={
          <>
            {fund.basis === 'FIXED_UNITS' &&
              fund.status !== 'WOUND_UP' &&
              fund.status !== 'RAISING' && (
                <Button
                  component={RouterLink}
                  to={`/manager/capital-returns/new?fund=${fund.id}`}
                  variant="outlined"
                >
                  Return capital
                </Button>
              )}
            {fund.basis === 'COMMITMENT' && (
              <Button
                component={RouterLink}
                to={`/manager/calls/new?fund=${fund.id}`}
                variant="outlined"
              >
                New capital call
              </Button>
            )}
            {fund.status !== 'WOUND_UP' && (
              <Button
                component={RouterLink}
                to={`/manager/distributions/new?fund=${fund.id}`}
                variant="contained"
              >
                New distribution
              </Button>
            )}
          </>
        }
      />
      <StatGrid>
        <StatCard
          label="Value on register"
          value={compactMoney(value)}
          hint={`Target ${compactMoney(fund.targetSize)}`}
        />
        <StatCard
          label="Holders"
          value={new Set(holdings.map((holding) => holding.holderId)).size}
          hint={`${holdings.length} holdings across classes`}
        />
        <StatCard
          label="Distributed FY2027 (net)"
          value={compactMoney(income)}
          hint={`${fund.distributionFrequency.toLowerCase()} frequency`}
        />
        {fund.basis === 'COMMITMENT' ? (
          <StatCard label="Uncalled commitments" value={compactMoney(uncalled)} tone="info" />
        ) : (
          <StatCard
            label="Registry live"
            value={date(fund.registryLiveDate)}
            hint={
              fund.migratedFrom ? `Migrated from ${fund.migratedFrom}` : 'Native from inception'
            }
          />
        )}
      </StatGrid>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tab} onChange={(_event, next) => setTab(next)} variant="scrollable">
          <Tab label="Register" />
          <Tab label="Classes & pricing" />
          <Tab label="Calls & distributions" />
          <Tab label="Fund settings" />
        </Tabs>
      </Box>
      {tab === 0 && <HoldingsTable holdings={holdings} showFund />}
      {tab === 1 && <FundClassesTab fund={fund} />}
      {tab === 2 && <FundEventsTab fund={fund} />}
      {tab === 3 && <FundSettingsTab fund={fund} />}
    </>
  );
}
